import { t } from '../i18nBridge';
import { conditionText, Graph, Step } from '../model';

/**
 * Ports of the journey editor (docs/pilot/SPEC.md §12.4): every exit of a step (and the trigger's «start») is a port
 * with a target step or null («конец ветки»). Pure: no React, no DOM — tested from apps/api with Jest.
 */

export const TRIGGER = '__trigger';

export type PortKey = 'start' | 'next' | 'on_event' | 'on_timeout' | 'else' | `cond:${number}` | `variant:${number}`;
export interface Port {
  step: string;
  key: PortKey;
}

export const portId = (p: Port) => `${p.step}:${p.key}`;
export function parsePortId(id: string): Port | null {
  const m = /^([a-z0-9_]{1,40}|__trigger):(start|next|on_event|on_timeout|else|cond:\d+|variant:\d+)$/.exec(id);
  return m ? { step: m[1], key: m[2] as PortKey } : null;
}
export const samePort = (a: Port | null | undefined, b: Port | null | undefined) => !!a && !!b && a.step === b.step && a.key === b.key;

/** Port keys of a step by its type; the trigger has the single port «start». */
export function portsOf(step: Step | typeof TRIGGER): PortKey[] {
  if (step === TRIGGER) return ['start'];
  switch (step.type) {
    case 'wait':
    case 'send_email':
    case 'send_notification':
    case 'webhook':
    case 'update_contact':
    case 'digest':
      return ['next'];
    case 'wait_for_event':
      return ['on_event', 'on_timeout'];
    case 'branch':
      return [...step.conditions.map((_, i) => `cond:${i}` as PortKey), 'else'];
    case 'split':
      return step.variants.map((_, i) => `variant:${i}` as PortKey);
    case 'exit':
      return [];
  }
}

export function portsOfNode(graph: Graph, id: string): Port[] {
  if (id === TRIGGER) return [{ step: TRIGGER, key: 'start' }];
  const s = graph.steps[id];
  return s ? portsOf(s).map((key) => ({ step: id, key })) : [];
}

const idx = (key: string) => Number(key.slice(key.indexOf(':') + 1));

export function targetOf(graph: Graph, port: Port): string | null {
  if (port.step === TRIGGER) return graph.start || null;
  const s = graph.steps[port.step];
  if (!s) return null;
  const k = port.key;
  if (k === 'next' && 'next' in s) return s.next;
  if (s.type === 'wait_for_event' && (k === 'on_event' || k === 'on_timeout')) return s[k];
  if (s.type === 'branch') return k === 'else' ? s.else : k.startsWith('cond:') ? (s.conditions[idx(k)]?.next ?? null) : null;
  if (s.type === 'split' && k.startsWith('variant:')) return s.variants[idx(k)]?.next ?? null;
  return null;
}

/** Mutates `graph` (callers pass a clone). */
export function setTarget(graph: Graph, port: Port, target: string | null): void {
  if (port.step === TRIGGER) {
    graph.start = target ?? '';
    return;
  }
  const s = graph.steps[port.step];
  if (!s) return;
  const k = port.key;
  if (k === 'next' && 'next' in s) s.next = target;
  else if (s.type === 'wait_for_event' && (k === 'on_event' || k === 'on_timeout')) s[k] = target;
  else if (s.type === 'branch' && k === 'else') s.else = target;
  else if (s.type === 'branch' && k.startsWith('cond:') && s.conditions[idx(k)]) s.conditions[idx(k)].next = target;
  else if (s.type === 'split' && k.startsWith('variant:') && s.variants[idx(k)]) s.variants[idx(k)].next = target;
}

/** Every port of the graph with its target (the trigger's first). */
export function allEdges(graph: Graph): { port: Port; to: string | null }[] {
  const out: { port: Port; to: string | null }[] = [{ port: { step: TRIGGER, key: 'start' }, to: graph.start || null }];
  for (const [id, s] of Object.entries(graph.steps)) for (const key of portsOf(s)) out.push({ port: { step: id, key }, to: targetOf(graph, { step: id, key }) });
  return out;
}

/** Ports that point at `id`. */
export function incoming(graph: Graph, id: string): Port[] {
  return allEdges(graph).filter((e) => e.to === id).map((e) => e.port);
}

/** Label in words: «есть событие», «тайм-аут», «вариант A 50%», «иначе», the condition. */
export function portLabel(graph: Graph, port: Port): string {
  if (port.step === TRIGGER) return t('старт');
  const s = graph.steps[port.step];
  const k = port.key;
  if (k === 'next') return t('дальше');
  if (k === 'on_event') return t('есть событие');
  if (k === 'on_timeout') return t('тайм-аут');
  if (k === 'else') return t('иначе');
  if (s?.type === 'branch' && k.startsWith('cond:')) {
    const c = s.conditions[idx(k)];
    return `${t('условие {n}', { n: idx(k) + 1 })}${c ? `: ${conditionText(c.when).slice(0, 60)}` : ''}`;
  }
  if (s?.type === 'split' && k.startsWith('variant:')) {
    const v = s.variants[idx(k)];
    return v ? t('вариант {id} {w}%', { id: v.id.toUpperCase(), w: v.weight }) : t('вариант');
  }
  return k;
}

/** A short label under the port handle (fits ~48 px). */
export function portShort(graph: Graph, port: Port): string {
  const k = port.key;
  if (k === 'start' || k === 'next') return '';
  if (k === 'on_event') return t('событие');
  if (k === 'on_timeout') return t('тайм-аут');
  if (k === 'else') return t('иначе');
  const s = graph.steps[port.step];
  if (s?.type === 'split' && k.startsWith('variant:')) {
    const v = s.variants[idx(k)];
    return v ? `${v.id.toUpperCase()} ${v.weight}%` : '';
  }
  return `${idx(k) + 1}`;
}

/** Validation path of a port (graph.validate.ts names edges like this). */
export function portPath(port: Port): string {
  if (port.step === TRIGGER) return 'start';
  const k = port.key;
  if (k.startsWith('cond:')) return `steps.${port.step}.conditions[${idx(k)}].next`;
  if (k.startsWith('variant:')) return `steps.${port.step}.variants[${idx(k)}].next`;
  return `steps.${port.step}.${k}`;
}
